import React from "react";
import type {
  TrainingTemplate,
  TrainingCourse,
  CreateTrainingTemplatePayload,
} from "../../../../api/training/Training.typed";
import type { QualificationItem } from "../../../../api/qualification/Qualification.typed";
import type { MailTemplate } from "../../../../api/mailTemplates/MailTemplate.typed";
import {
  useTraining,
  useConfig,
  useQualification,
  useMailTemplate,
} from "../../../../contexts/api";
import useAppSettings from "../../../../contexts/app/appSettings";
import { useAlert } from "../../../../contexts/app/notificationToast";
import { usePermissions, Permission } from "../../../../contexts/app/permissions";
import { extractDataFromResponse } from "../../../../utils/paginationHelpers";
import { isPdfFile } from "../trainingPdfFile";
import {
  trainingTemplatesEditorInitialState,
  trainingTemplatesEditorReducer,
} from "./trainingTemplatesEditorReducer";

export const TRAINING_TEMPLATE_ROW_ACTIONS_WIDTH = 128;

export function useTrainingTemplatesEditor() {
  const [state, dispatch] = React.useReducer(
    trainingTemplatesEditorReducer,
    trainingTemplatesEditorInitialState,
  );
  const { items: templates, draft: selectedTemplate, selectedId } = state;

  const [isLoading, setIsLoading] = React.useState(false);
  const [newCardName, setNewCardName] = React.useState("");
  const [courses, setCourses] = React.useState<TrainingCourse[] | null>(null);
  const [isCoursesLoading, setIsCoursesLoading] = React.useState(false);
  const [newCourseName, setNewCourseName] = React.useState("");
  const [newCourseFile, setNewCourseFile] = React.useState<File | null>(null);
  const [addingCourse, setAddingCourse] = React.useState(false);
  const [deletingCourseId, setDeletingCourseId] = React.useState<string | null>(null);
  const [replacingCourseId, setReplacingCourseId] = React.useState<string | null>(null);
  const [savingCourseId, setSavingCourseId] = React.useState<string | null>(null);
  const [editingCourseId, setEditingCourseId] = React.useState<string | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [extCredits, setExtCredits] = React.useState<{ ID: number; Name: string }[]>([]);
  const [extQualifications, setExtQualifications] = React.useState<{ ID: number; Name: string }[]>(
    [],
  );
  const [localCatalogQualifications, setLocalCatalogQualifications] = React.useState<
    QualificationItem[]
  >([]);
  const [mailTemplates, setMailTemplates] = React.useState<MailTemplate[]>([]);

  const inputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});

  const {
    getTrainingsTemplates,
    updateTrainingTemplate,
    deleteTrainingTemplate,
    listTrainingCourses,
    addTrainingCourse,
    deleteTrainingCourseSilent,
    replaceTrainingCourses,
    uploadTrainingTemplateResource,
    createTrainingTemplate,
    getTrainingTemplatesExternalOptions,
  } = useTraining();
  const { listMailTemplates } = useMailTemplate();
  const { getConfig } = useConfig();
  const { listQualificationItems } = useQualification();
  const { config } = useAppSettings();
  const { showAlert } = useAlert();
  const { hasPermission } = usePermissions();

  const canEdit = hasPermission(Permission.TRAININGS_EDIT);
  const showLocalDestinationQualification = config?.CustomersSync !== true;
  const showExternalIdentifiers = config?.CustomersSync === true;

  const sortedLocalQualifications = React.useMemo(
    () =>
      [...localCatalogQualifications].sort((itemA, itemB) =>
        (itemA.sigle || "").localeCompare(itemB.sigle || "", undefined, { sensitivity: "base" }),
      ),
    [localCatalogQualifications],
  );

  const reloadItems = React.useCallback(async () => {
    const result = await getTrainingsTemplates();
    dispatch({
      type: "LOAD_ITEMS",
      items: extractDataFromResponse<TrainingTemplate>(result) ?? [],
    });
  }, [getTrainingsTemplates]);

  React.useEffect(() => {
    if (!showLocalDestinationQualification) {
      setLocalCatalogQualifications([]);
      return;
    }
    void listQualificationItems().then((rows) => setLocalCatalogQualifications(rows ?? []));
  }, [showLocalDestinationQualification, listQualificationItems]);

  React.useEffect(() => {
    setIsLoading(true);
    void reloadItems().finally(() => setIsLoading(false));
  }, [reloadItems]);

  React.useEffect(() => {
    let cancelled = false;

    void listMailTemplates("training").then((list) => {
      if (!cancelled && list) setMailTemplates(list);
    });

    return () => {
      cancelled = true;
    };
  }, [listMailTemplates]);

  React.useEffect(() => {
    getConfig?.().then((cfg) => {
      const hasZuurit =
        cfg?.ZuuritConfig &&
        (cfg.ZuuritConfig.Server || cfg.ZuuritConfig.Email || cfg.ZuuritConfig.Password);
      if (!hasZuurit) {
        setExtCredits([]);
        setExtQualifications([]);
        return;
      }
      getTrainingTemplatesExternalOptions?.("zuurit")
        .then((res) => {
          setExtCredits(res?.Credits ?? []);
          setExtQualifications(res?.Qualifications ?? []);
        })
        .catch(() => {});
    });
  }, [getConfig, getTrainingTemplatesExternalOptions]);

  const handleChangeTemplate = React.useCallback((newValue: string | null) => {
    const id = newValue != null && newValue !== "" ? Number(newValue) : null;
    dispatch({ type: "SELECT", id: id != null && Number.isFinite(id) ? id : null });
  }, []);

  React.useEffect(() => {
    if (selectedId == null) {
      setCourses(null);
      return;
    }
    setIsCoursesLoading(true);
    listTrainingCourses(selectedId)
      .then((list) => setCourses(list ?? []))
      .finally(() => setIsCoursesLoading(false));
  }, [selectedId, listTrainingCourses]);

  const handleFieldChange = React.useCallback(
    (field: keyof Omit<TrainingTemplate, "ID">, value: string | number | boolean | null) => {
      dispatch({ type: "UPDATE_FIELD", field, value });
    },
    [],
  );

  const handleMailTemplateChange = React.useCallback(
    (field: keyof Omit<TrainingTemplate, "ID">, templateId: number) => {
      dispatch({ type: "UPDATE_FIELD", field, value: templateId > 0 ? templateId : null });
    },
    [],
  );

  const handleAddCard = React.useCallback(() => {
    if (newCardName.trim() !== "" && selectedTemplate) {
      dispatch({ type: "ADD_CARD", cardName: newCardName.trim() });
      setNewCardName("");
    }
  }, [newCardName, selectedTemplate]);

  const handleRemoveCard = React.useCallback((index: number) => {
    dispatch({ type: "REMOVE_CARD", index });
  }, []);

  const handleSaveTemplate = React.useCallback(async () => {
    if (!selectedTemplate) return;
    const saved = await updateTrainingTemplate(selectedTemplate);
    if (!saved) return;
    dispatch({ type: "COMMIT_DRAFT" });
  }, [selectedTemplate, updateTrainingTemplate]);

  const handleDeleteTemplate = React.useCallback(async () => {
    if (selectedId == null) return;
    const deleted = await deleteTrainingTemplate(selectedId);
    if (!deleted) return;
    dispatch({ type: "REMOVE_ITEM", id: selectedId });
    await reloadItems();
  }, [selectedId, deleteTrainingTemplate, reloadItems]);

  const handleOpenCreate = React.useCallback(() => setIsCreateOpen(true), []);

  const handleSubmitCreate = React.useCallback(
    async (payload: CreateTrainingTemplatePayload) => {
      try {
        setIsLoading(true);
        const created = await createTrainingTemplate(payload);
        await reloadItems();
        const toSelect =
          created && typeof created === "object" && "ID" in created ? created.ID : undefined;
        if (typeof toSelect === "number") {
          dispatch({ type: "SELECT", id: toSelect });
        }
        setIsCreateOpen(false);
      } catch {
        showAlert("Création échouée", "danger");
      } finally {
        setIsLoading(false);
      }
    },
    [createTrainingTemplate, reloadItems, showAlert],
  );

  const handleAddCourse = React.useCallback(async () => {
    if (!selectedTemplate || !newCourseFile) return;
    if (!isPdfFile(newCourseFile)) {
      showAlert("Seuls les fichiers PDF sont acceptés", "warning");
      return;
    }
    try {
      setIsUploading(true);
      setAddingCourse(true);
      const created = await addTrainingCourse(
        selectedTemplate.ID,
        newCourseFile,
        newCourseName || undefined,
      );
      setCourses((prev) => (created != null ? [...(prev ?? []), created] : (prev ?? null)));
      await listTrainingCourses(selectedTemplate.ID).then((list) => setCourses(list ?? []));
      setNewCourseFile(null);
      setNewCourseName("");
    } catch {
      showAlert("Échec de l'ajout du cours", "danger");
    } finally {
      setAddingCourse(false);
      setIsUploading(false);
    }
  }, [
    selectedTemplate,
    newCourseFile,
    newCourseName,
    addTrainingCourse,
    listTrainingCourses,
    showAlert,
  ]);

  const handleDeleteCourse = React.useCallback(
    async (course: TrainingCourse) => {
      if (!selectedTemplate) return;
      try {
        setDeletingCourseId(course.ID);
        await deleteTrainingCourseSilent(selectedTemplate.ID, course.ID);
        setCourses((prev) => (prev ?? []).filter((c) => c.ID !== course.ID));
      } catch {
        showAlert("Échec de la suppression du cours", "danger");
      } finally {
        setDeletingCourseId(null);
      }
    },
    [selectedTemplate, deleteTrainingCourseSilent, showAlert],
  );

  const handleReplaceCourse = React.useCallback(
    async (course: TrainingCourse, file?: File | null) => {
      if (!selectedTemplate || !file) return;
      if (!isPdfFile(file)) {
        showAlert("Seuls les fichiers PDF sont acceptés", "warning");
        return;
      }
      try {
        setIsUploading(true);
        setReplacingCourseId(course.ID);
        const created = await addTrainingCourse(selectedTemplate.ID, file, course.Name);
        setCourses((prev) => {
          const list = prev ?? [];
          return list
            .map((c) => (c.ID === course.ID ? created : c))
            .filter((c): c is TrainingCourse => c != null);
        });
        await deleteTrainingCourseSilent(selectedTemplate.ID, course.ID);
      } catch {
        showAlert("Échec du remplacement du cours", "danger");
      } finally {
        setReplacingCourseId(null);
        setIsUploading(false);
      }
    },
    [selectedTemplate, addTrainingCourse, deleteTrainingCourseSilent, showAlert],
  );

  const handleSaveCourseName = React.useCallback(
    async (course: TrainingCourse) => {
      if (!selectedTemplate) return;
      try {
        setSavingCourseId(course.ID);
        await replaceTrainingCourses(selectedTemplate.ID, courses ?? []);
      } finally {
        setSavingCourseId(null);
      }
    },
    [selectedTemplate, replaceTrainingCourses, courses],
  );

  const handleUploadTemplateFile = React.useCallback(
    async (field: "agreement" | "disclaimer", file?: File | null) => {
      if (!selectedTemplate || !file) return;
      if (!isPdfFile(file)) {
        showAlert("Seuls les fichiers PDF sont acceptés", "warning");
        return;
      }
      try {
        setIsUploading(true);
        const updated = await uploadTrainingTemplateResource(selectedTemplate.ID, field, file);
        if (updated) {
          dispatch({ type: "PATCH_ITEM", item: updated });
        }
        showAlert("Fichier importé avec succès", "success");
      } catch {
        showAlert("Échec de l'upload du fichier", "danger");
      } finally {
        setIsUploading(false);
      }
    },
    [selectedTemplate, uploadTrainingTemplateResource, showAlert],
  );

  const preserveFocus = React.useCallback((field: string, callback: () => void) => {
    const currentInput = inputRefs.current[field];
    const isFocused = currentInput === document.activeElement;
    callback();
    if (isFocused && currentInput) {
      setTimeout(() => {
        currentInput.focus();
        const length = currentInput.value.length;
        currentInput.setSelectionRange(length, length);
      }, 0);
    }
  }, []);

  const handleFieldChangeWithFocus = React.useCallback(
    (field: keyof Omit<TrainingTemplate, "ID">, value: string | number | boolean) => {
      preserveFocus(String(field), () => {
        dispatch({ type: "UPDATE_FIELD", field, value });
      });
    },
    [preserveFocus],
  );

  return {
    templates,
    selectedTemplate,
    isLoading,
    newCardName,
    setNewCardName,
    courses,
    setCourses,
    isCoursesLoading,
    newCourseName,
    setNewCourseName,
    newCourseFile,
    setNewCourseFile,
    addingCourse,
    deletingCourseId,
    replacingCourseId,
    savingCourseId,
    editingCourseId,
    setEditingCourseId,
    isUploading,
    isCreateOpen,
    setIsCreateOpen,
    extCredits,
    extQualifications,
    showLocalDestinationQualification,
    showExternalIdentifiers,
    sortedLocalQualifications,
    mailTemplates,
    canEdit,
    inputRefs,
    handleChangeTemplate,
    handleFieldChange,
    handleMailTemplateChange,
    handleFieldChangeWithFocus,
    handleAddCard,
    handleRemoveCard,
    handleSaveTemplate,
    handleDeleteTemplate,
    handleOpenCreate,
    handleSubmitCreate,
    handleAddCourse,
    handleDeleteCourse,
    handleReplaceCourse,
    handleSaveCourseName,
    handleUploadTemplateFile,
  };
}
